import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  defaultSignatureFromAdmin,
  getSignature,
  setSignature,
} from "../../utils/emailSignature";

// Editable email signature. HTML paste from Gmail is supported as-is. The
// preview renders the signature exactly as it will appear in the outgoing
// message, so "what you see here" is "what the recipient sees."
export default function SignatureEditor() {
  const adminInfo = useSelector((s) => s.login?.adminInfo || {});
  const ownerEmail = adminInfo?.gmailPrimaryAddress || adminInfo?.email || "";
  const starter = useMemo(() => defaultSignatureFromAdmin(adminInfo), [adminInfo]);

  const [html, setHtml] = useState(() => getSignature(ownerEmail) || starter);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    const stored = getSignature(ownerEmail);
    setHtml(stored || starter);
  }, [ownerEmail, starter]);

  const onSave = () => {
    setSignature(ownerEmail, html.trim());
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  };

  const onReset = () => {
    setHtml(starter);
  };

  const onClear = () => {
    setHtml("");
    setSignature(ownerEmail, "");
  };

  return (
    <section className="rounded-2xl border border-zinc-100 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Email signature</h2>
          <p className="mt-0.5 text-sm text-zinc-500">
            Used on every message you send from the Leads workspace. Paste the HTML of your Gmail signature below — nothing else is appended.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {savedFlash ? (
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              Saved
            </span>
          ) : null}
          <button
            type="button"
            onClick={onReset}
            className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
          >
            Use suggested
          </button>
          <button
            type="button"
            onClick={onClear}
            className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onSave}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
          >
            Save signature
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500">
            HTML source
          </label>
          <textarea
            value={html}
            onChange={(event) => setHtml(event.target.value)}
            rows={10}
            spellCheck={false}
            placeholder={'<div>Your Name<br/>Title<br/><a href="mailto:you@domain.com">you@domain.com</a></div>'}
            className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 font-mono text-xs text-zinc-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
          <p className="mt-2 text-[11px] leading-4 text-zinc-500">
            Tip: in Gmail → Settings → See all settings → Signature → right-click the signature block → Inspect → copy the element's HTML and paste it here.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Preview
          </label>
          <div className="mt-1 min-h-[210px] rounded-lg border border-zinc-200 bg-zinc-50/60 px-4 py-3">
            {html.trim() ? (
              <div className="prose prose-sm max-w-none text-zinc-800" dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <p className="text-xs italic text-zinc-500">
                No signature saved — outbound emails will ship without one. If the backend used to auto-generate one, that is now disabled per-send.
              </p>
            )}
          </div>
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-4 text-amber-900">
            <p className="font-semibold">Keep it light.</p>
            <p className="mt-0.5">
              Avoid embedded images (they trip spam filters and get stripped). Plain text with a mailto link deliverability-wise is the safest. Title, phone, and email is enough.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
